import React, { useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEmailConfirmation } from '../../hooks/useEmailConfirmation';

interface RegisterFormProps {
  onBackClick: () => void;
  onRegisterSuccess?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onBackClick, onRegisterSuccess }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [confirmationSent, setConfirmationSent] = useState(false);
  
  const { sendConfirmationEmail, loading, error } = useEmailConfirmation();

  const validateEmail = (email: string) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    
    // Send confirmation email
    const result = await sendConfirmationEmail({ email });
    
    if (result) {
      setConfirmationSent(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleEmailSubmit} sx={{ width: '100%' }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBackClick}
        sx={{ 
          mb: 4,
          color: 'text.secondary',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main',
          }
        }}
      >
        Back
      </Button>

      {/* Title */}
      <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
        Create your account
      </Typography>

      {/* Success Message */}
      {confirmationSent && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Confirmation email sent! Please check your inbox and follow the link to complete your registration.
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!confirmationSent && (
        <>
          {/* Email Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email address"
            name="email"
            autoComplete="email"
            autoFocus
            sx={{ mb: 2 }}
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
          />
          {/* Continue Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 8,
              backgroundColor: '#00A3FF',
              '&:hover': {
                backgroundColor: '#0091E6'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send confirmation email'}
          </Button>
        </>
      )}

      {/* Terms Text */}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
        By creating an account, you agree to our Terms of Service and Privacy Policy.
      </Typography>
    </Box>
  );
};

export default RegisterForm;
